/* MECASY delivery: isolated UI, native Shopify Markets and shipping-rate endpoints. */
(() => {
  if (customElements.get('mcy-shipping-rates')) return;
  const countries = new Set(['BE', 'FR', 'LU', 'NL', 'DE', 'AT']);
  const dayInBrussels = (now) => {
    const parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Brussels', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
    const value = (type) => Number(parts.find(p => p.type === type).value);
    return new Date(Date.UTC(value('year'), value('month') - 1, value('day'), 12));
  };
  const businessDate = (start, days) => {
    const date = new Date(start);
    while (days > 0) { date.setUTCDate(date.getUTCDate() + 1); if (![0, 6].includes(date.getUTCDay())) days--; }
    return date;
  };
  class DeliveryDate extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => { if (!this.isConnected) return; this.update(); clearInterval(this.timer); this.timer = setInterval(() => this.update(), 60000); });
    }
    disconnectedCallback() { clearInterval(this.timer); }
    update(now = new Date()) {
      const target = this.querySelector('[data-date-text]'); if (!target) return;
      target.textContent = this.dataset.fallback || 'Livraison calculée selon votre pays';
      const min = Number(this.dataset.min), max = Number(this.dataset.max);
      if (!countries.has(this.dataset.country) || !Number.isInteger(min) || !Number.isInteger(max) || min < 1 || max < min || max > 30) return;
      try {
        const start = dayInBrussels(now), first = businessDate(start, min), last = businessDate(start, max);
        const sameMonth = first.getUTCMonth() === last.getUTCMonth() && first.getUTCFullYear() === last.getUTCFullYear();
        const year = first.getUTCFullYear() !== last.getUTCFullYear() || last.getUTCFullYear() !== start.getUTCFullYear();
        const format = d => new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', ...(year ? { year: 'numeric' } : {}), timeZone: 'UTC' }).format(d);
        const firstText = sameMonth ? String(first.getUTCDate()) : format(first);
        target.textContent = (this.dataset.message || 'Livraison du {date_min} au {date_max}').replaceAll('{date_min}', firstText).replaceAll('{date_max}', format(last));
      } catch (_) { /* Keep the merchant's fallback. */ }
    }
  }
  customElements.define('mcy-delivery-date', DeliveryDate);

  const widgets = new Set(), cache = new Map(), pending = new Map(), postcodes = new Map();
  const postcodeStorageKey = 'mecasy-postcodes';
  // Explicit country choices synchronize the estimate and Shopify Markets.
  const destinationStorageKey = 'mecasy-shipping-destination';
  const scrollStorageKey = 'mecasy-cart-country-scroll';
  let countryNavigationPending = false;
  let selectedDestination = null;
  try {
    const saved = JSON.parse(sessionStorage.getItem(destinationStorageKey) || 'null');
    if (saved && typeof saved.country === 'string' && typeof saved.market === 'string') selectedDestination = saved;
  } catch (_) {}

try {
  const savedPostcodes = JSON.parse(sessionStorage.getItem(postcodeStorageKey) || '{}');

  Object.entries(savedPostcodes).forEach(([country, zip]) => {
    if (countries.has(country) && typeof zip === 'string' && zip.trim()) {
      postcodes.set(country, zip.trim());
    }
  });
} catch (_) {}
  function restoreDestination(marketCountry, hasDestination) {
    // An explicit header/drawer choice supersedes a previous cart estimate,
    // even when Shopify returns the same market country after navigation.
    const explicit = selectedDestination?.source === 'localization';
    const country = (explicit || selectedDestination?.market === marketCountry)
      && hasDestination(selectedDestination?.country)
      ? selectedDestination.country : marketCountry;
    // Consume the one-time choice and bind it to this page's actual market.
    // Persist market changes too, so an old estimate cannot reappear later.
    selectedDestination = { country, market: marketCountry };
    try { sessionStorage.setItem(destinationStorageKey, JSON.stringify(selectedDestination)); } catch (_) {}
    return country;
  }
  const totals = new Map(), choices = new Map();
  const money = (cents, currency) => new Intl.NumberFormat(document.documentElement.lang || 'fr', { style: 'currency', currency }).format(cents / 100);
  function setTotal(instance, total) {
    if (total) totals.set(instance, total); else totals.delete(instance);
    document.querySelectorAll('mcy-shipping-total').forEach(el => { if (el.dataset.instance === instance) el.update(); });
  }
  class ShippingTotal extends HTMLElement {
    connectedCallback() { queueMicrotask(() => { if (this.isConnected) this.update(); }); }
    update() {
      const total = totals.get(this.dataset.instance); this.hidden = !total; this.toggleAttribute('data-ready', !!total);
      if (!total) return;
      this.querySelector('[data-subtotal]').textContent = money(total.subtotal, total.currency);
      this.querySelector('[data-shipping]').textContent = money(total.shipping, total.currency);
      this.querySelector('[data-grand-total]').textContent = money(total.subtotal + total.shipping, total.currency);
    }
  }
  customElements.define('mcy-shipping-total', ShippingTotal);
  let queue = Promise.resolve();
  const pause = ms => new Promise(resolve => setTimeout(resolve, ms));
  async function jsonRequest(url, method = 'GET') {
    const controller = new AbortController(), timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(url, { method, credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }, signal: controller.signal });
      const body = await response.json();
      if (!response.ok) { const error = new Error('Shipping request failed'); error.status = response.status; error.body = body; throw error; }
      return body;
    } finally { clearTimeout(timeout); }
  }
  function ratesRequest(root, country, zip, cart) {
    const key = JSON.stringify([root, country, zip, cart.currency, cart.total_price, cart.total_weight, cart.items.map(item => [item.key, item.quantity, item.final_line_price])]);
    const saved = cache.get(key); if (saved && Date.now() - saved.time < 30000) return Promise.resolve(saved.data);
    if (pending.has(key)) return pending.get(key);
    // Shopify's asynchronous calculation is cart-scoped: serialize distinct requests.
    const task = queue.catch(() => {}).then(async () => {
      const params = new URLSearchParams({ 'shipping_address[country]': country });
      if (zip) params.set('shipping_address[zip]', zip);
      await jsonRequest(`${root}cart/prepare_shipping_rates.json?${params}`, 'POST');
      for (let i = 0; i < 5; i++) {
        if (i) await pause(Math.min(500 * (2 ** (i - 1)), 3000));
        const data = await jsonRequest(`${root}cart/async_shipping_rates.json?${params}`);
        if (data !== null) {
          if (!Array.isArray(data.shipping_rates)) throw new Error('Invalid rates response');
          cache.set(key, { time: Date.now(), data }); if (cache.size > 20) cache.delete(cache.keys().next().value);
          return data;
        }
      }
      throw new Error('Rates calculation timed out');
    });
    pending.set(key, task); queue = task.catch(() => {});
    task.then(() => pending.delete(key), () => pending.delete(key));
    return task;
  }
  class ShippingRates extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected || this.ready) return;
        this.ready = true; this.version = this.version || 0; this.zip = postcodes.get(this.dataset.country) || '';
        this.status = this.querySelector('[data-rates-status]'); this.list = this.querySelector('[data-rates-list]');
        this.postalForm = this.querySelector('[data-postal-form]'); this.postal = this.querySelector('[data-postal]');
        this.retry = this.querySelector('[data-retry]'); this.edit = this.querySelector('[data-edit-postal]');
        if (!this.status || !this.list || !this.postalForm) return;
        this.events = new AbortController(); const options = { signal: this.events.signal };
        this.marketCountry = this.marketCountry || this.dataset.country;
        this.countrySelect = this.querySelector('select[name="mcy_shipping_country"]');
        this.countryHeading = this.querySelector('[data-shipping-heading]');
        const applyCountry = this.querySelector('[data-apply-country]');
        const changeCountry = () => this.changeCountry();
        this.countrySelect?.addEventListener('change', changeCountry, options);
        applyCountry?.addEventListener('click', changeCountry, options);
        if (this.countrySelect && applyCountry) applyCountry.hidden = true;
        const restored = restoreDestination(this.marketCountry, country => this.hasDestination(country));
        this.applyDestination(restored, false);
        this.postalForm.addEventListener('submit', event => {
          event.preventDefault(); this.zip = this.postal.value.trim();
          if (!this.zip) { this.postal.focus(); return; }
          this.awaitPostal = false;
          const address = new URL(window.location.href);
          address.searchParams.delete('mcy_postal');
          window.history.replaceState(window.history.state, '', address.pathname + address.search + address.hash);
postcodes.set(this.dataset.country, this.zip);

try {
  sessionStorage.setItem(
    postcodeStorageKey,
    JSON.stringify(Object.fromEntries(postcodes))
  );
} catch (_) {}

cache.clear();          widgets.forEach(widget => { if (widget.dataset.country === this.dataset.country) { widget.zip = this.zip; widget.postal.value = this.zip; widget.refresh(); } });
        }, options);
        this.retry.addEventListener('click', () => { cache.clear(); this.refresh(); }, options);
        this.edit.addEventListener('click', () => { this.postalForm.hidden = false; this.postal.focus(); }, options);
        widgets.add(this);
        this.initialRefreshDone = false;
        this.observer = new IntersectionObserver(entries => {
  if (entries.some(entry => entry.isIntersecting) && !this.initialRefreshDone) {
    this.initialRefreshDone = true;
    this.refresh();
  }
});

this.observer.observe(this);

      });
    }
    changeCountry() {
      const scrollYAtChange = window.scrollY;
      const country = this.countrySelect?.value;
      if (!this.hasDestination(country) || countryNavigationPending) return;
      // Save a draft against its OLD country before changing destination.
      const oldCountry = this.dataset.country;
      const draft = this.postal.value.trim();
      if (draft) postcodes.set(oldCountry, draft);
      else postcodes.delete(oldCountry);
      try { sessionStorage.setItem(postcodeStorageKey, JSON.stringify(Object.fromEntries(postcodes))); } catch (_) {}

      const form = document.getElementById('MecasyCartCountryForm');
      const input = form?.querySelector('input[name="country_code"]');
      const returnTo = form?.querySelector('input[name="return_to"]');
      const switchMarket = country !== this.marketCountry && !!input && !!returnTo;
      selectedDestination = { country, market: this.marketCountry, ...(switchMarket ? { source: 'localization' } : {}) };
      try { sessionStorage.setItem(destinationStorageKey, JSON.stringify(selectedDestination)); } catch (_) {}
      widgets.forEach(widget => {
        if (widget.marketCountry === this.marketCountry) widget.applyDestination(country, !switchMarket);
      });
      if (!switchMarket) return;

      // Use Shopify's native localization form; never change just the flag.
      // Keep the current page/preview, removing stale country overrides only.
      const address = new URL(window.location.href);
      ['country', 'country_code', 'mcy_postal'].forEach(key => address.searchParams.delete(key));
      input.value = country;
      returnTo.value = address.pathname + address.search + address.hash;
      countryNavigationPending = true;
      if (this.classList.contains('mcy-shipping--cart')) {
        try {
          sessionStorage.setItem(scrollStorageKey, JSON.stringify({
            y: scrollYAtChange,
            pending: true,
            restoration: history.scrollRestoration || 'auto',
            country,
            time: Date.now()
          }));
          history.scrollRestoration = 'manual';
        } catch (_) { /* Storage must never prevent the native country submit. */ }
      }
      try {
        HTMLFormElement.prototype.submit.call(form);
      } catch (_) {
        if (this.classList.contains('mcy-shipping--cart')) {
          try {
            const saved = JSON.parse(sessionStorage.getItem(scrollStorageKey) || 'null');
            if (saved) history.scrollRestoration = saved.restoration || 'auto';
            sessionStorage.removeItem(scrollStorageKey);
          } catch (_) {}
        }
        countryNavigationPending = false;
        widgets.forEach(widget => { if (widget.marketCountry === this.marketCountry) widget.refresh(); });
      }
    }
    hasDestination(country) {
      return !!this.countrySelect && Array.from(this.countrySelect.options).some(option => option.value === country);
    }
    applyDestination(country, refresh = true) {
      if (!this.hasDestination(country)) return;
      this.dataset.country = country;
      this.countrySelect.value = country;
      this.zip = postcodes.get(country) || '';
      this.postal.value = this.zip;
      if (this.classList.contains('mcy-shipping--cart')) {
        this.postal.placeholder = ({ BE: 'Ex. 7000', FR: 'Ex. 75001', LU: 'Ex. 1616' })[country] || 'Code postal';
      }
      this.awaitPostal = !this.zip;
      this.postalForm.hidden = !this.awaitPostal;
      const label = this.countrySelect.selectedOptions[0]?.textContent.trim() || country;
      if (this.countryHeading) this.countryHeading.textContent = (this.dataset.titleTemplate || 'Livraison vers {pays}').replaceAll('{pays}', label);
      this.clearTotal();
      this.list.replaceChildren(); this.list.hidden = true;
      this.edit.hidden = true; this.retry.hidden = true;
      if (refresh) this.refresh();
    }
    disconnectedCallback() {
      this.version++; this.ready = false; this.events?.abort(); this.observer?.disconnect(); clearTimeout(this.debounce); widgets.delete(this); this.clearTotal();
    }
    clearTotal() { this.quote = null; setTotal(this.dataset.instance, null); }
    choose(key) {
      const rate = this.quote?.rates.find(rate => rate.key === key); if (!rate) return;
      this.list.querySelectorAll('input[type=radio]').forEach(input => { input.checked = input.value === key; });
      setTotal(this.dataset.instance, { subtotal: this.quote.subtotal, shipping: rate.cents, currency: this.quote.currency });
    }
    invalidate() {
      this.clearTotal();
      this.version++; this.list?.replaceChildren(); if (this.list) this.list.hidden = true;
      this.hidden = false;
      if (this.status) { this.status.hidden = false; this.status.textContent = 'Actualisation des frais…'; }
      clearTimeout(this.debounce); this.debounce = setTimeout(() => { if (this.getClientRects().length && this.closest('cart-drawer')?.classList.contains('active') !== false) this.refresh(); }, 300);
    }
    async refresh() {
      if (!this.isConnected || !this.ready) return;
      this.clearTotal();
      const version = ++this.version; const current = () => this.isConnected && this.version === version;
      this.list.replaceChildren(); this.list.hidden = true; this.status.hidden = false; this.retry.hidden = true; this.edit.hidden = true;
      if (!countries.has(this.dataset.country)) { this.status.textContent = 'Frais de livraison calculés au paiement pour cette destination.'; return; }
      const root = this.dataset.root;
      if (!root || !root.startsWith('/') || root.startsWith('//')) { this.status.textContent = 'Les frais seront confirmés au paiement.'; return; }
      const prefix = root.endsWith('/') ? root : root + '/';
      this.status.textContent = 'Calcul des frais de livraison…'; this.setAttribute('aria-busy', 'true');
      const submit = this.postalForm.querySelector('button'); submit.disabled = true;
      try {
        const cart = await jsonRequest(`${prefix}cart.js`); if (!current()) return;
        if (!cart.item_count || !cart.requires_shipping) { this.hidden = true; return; }
        this.hidden = false;
        if (this.awaitPostal && !this.zip) {
          this.status.textContent = ''; this.status.hidden = true;
          this.postalForm.hidden = false; return;
        }
        const data = await ratesRequest(prefix, this.dataset.country, this.zip, cart); if (!current()) return;
        if (!data.shipping_rates.length) {
          this.status.textContent = this.zip ? 'Aucun tarif retourné pour cette destination. Vérifiez le code postal ou poursuivez au paiement.' : '';
          this.status.hidden = !this.zip;
          this.postalForm.hidden = false; return;
        }
        const currency = cart.currency;
        if (!Number.isSafeInteger(cart.total_price) || cart.total_price < 0) throw new Error('Invalid cart total');
        const validRates = [];
        const choiceKey = JSON.stringify([this.dataset.country, this.zip, currency]);
        const rows = data.shipping_rates.map((rate, index) => {
          if (typeof rate.price !== 'string' && typeof rate.price !== 'number') throw new Error('Invalid price');
          const amount = Number(rate.price);
          if (String(rate.price).trim() === '' || !Number.isFinite(amount) || amount < 0 || !/^[A-Z]{3}$/.test(currency) || (rate.currency && rate.currency !== currency)) throw new Error('Unconfirmed currency or price');
          const cents = Math.round(amount * 100);
          if (!Number.isSafeInteger(cents) || !Number.isSafeInteger(cart.total_price + cents)) throw new Error('Invalid total');
          const key = JSON.stringify([rate.code || rate.name, rate.source || '', rate.name || '', index]);
          validRates.push({ key, cents });
          const li = document.createElement('li'), label = document.createElement('label'), text = document.createElement('span'), price = document.createElement('strong'), input = document.createElement('input');
          input.type = 'radio'; input.name = `mcy-rate-${this.dataset.instance}`; input.value = key;
          text.textContent = rate.presentment_name || rate.name || 'Livraison';
          price.textContent = money(cents, currency);
          input.addEventListener('change', () => {
            choices.set(choiceKey, key);
            widgets.forEach(widget => { if (widget.quote?.choiceKey === choiceKey) widget.choose(key); });
          });
          label.append(input, text, price); li.append(label); return li;
        });
        this.list.replaceChildren(...rows); this.list.hidden = false; this.status.textContent = ''; this.status.hidden = true;
        this.quote = { rates: validRates, subtotal: cart.total_price, currency, choiceKey };
        const selected = validRates.find(rate => rate.key === choices.get(choiceKey)) || validRates.reduce((a, b) => a.cents <= b.cents ? a : b);
        choices.set(choiceKey, selected.key); this.choose(selected.key);
        this.postalForm.hidden = true; this.edit.hidden = false;
        this.edit.textContent = this.zip ? (this.dataset.postalSummary || 'Code postal : {code} · Modifier').replaceAll('{code}', this.zip) : (this.dataset.editLabel || 'Modifier le code postal');
      } catch (error) {
        if (!current()) return;
        this.list.replaceChildren(); this.list.hidden = true;
        if (error.status === 422) {
          this.status.textContent = this.zip ? 'Vérifiez votre code postal.' : '';
          this.status.hidden = !this.zip;
          this.postalForm.hidden = false;
        } else {
          this.status.textContent = 'Calcul indisponible pour le moment. Les frais seront confirmés au paiement.'; this.retry.hidden = false;
        }
      } finally { if (current()) { this.removeAttribute('aria-busy'); submit.disabled = false; } }
    }
  }
  customElements.define('mcy-shipping-rates', ShippingRates);
  // Ignite global.js broadcasts these native events; no interception of cart mutations.
  ['cart-update', 'cart-add', 'cart-remove'].forEach(name => document.addEventListener(name, () => { cache.clear(); widgets.forEach(widget => widget.invalidate()); }));
  document.addEventListener('cart-drawer-open', () => widgets.forEach(widget => { if (widget.closest('cart-drawer')) widget.refresh(); }));
  window.addEventListener('pageshow', event => { if (event.persisted) { countryNavigationPending = false; cache.clear(); widgets.forEach(widget => widget.refresh()); } });
})();
